import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAccessories } from '@/services/accessories';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Accessory } from '@/types/models';
import { TableSkeleton } from '@/components/common/Skeleton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { Modal } from '@/components/common/Modal';
import { AccessoryForm } from '@/features/accessories/AccessoryForm';
import { AccessoryDetail } from '@/features/accessories/AccessoryDetail';
import { useAuth } from '@/context/AuthContext';
import { Package, Plus, AlertTriangle } from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

export const AccessoriesPage: React.FC = () => {
  const { hasPermission, role } = useAuth();
  const isOwner = role === 'owner';
  const isStaff = !hasPermission('purchases');
  const canViewCosts = hasPermission('purchases');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [page, setPage] = useState(0);

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAccessoryId, setSelectedAccessoryId] = useState<string | null>(null);

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    setPage(0);
    const timer = setTimeout(() => setDebouncedSearch(val), 400);
    return () => clearTimeout(timer);
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.accessories.list({
      search: debouncedSearch,
      category: categoryFilter,
      lowStockOnly,
      archiveFilter,
      page,
    }),
    queryFn: () =>
      fetchAccessories({
        search: debouncedSearch,
        category: categoryFilter,
        lowStockOnly,
        archiveFilter,
        page,
        isStaff,
      }),
    placeholderData: (prev) => prev,
  });

  const accessories = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-black flex items-center space-x-2">
            <Package className="w-5 h-5 text-brand-green" />
            <span>Accessories & Spare Parts</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {totalCount > 0 ? `${totalCount} catalogue items` : 'Manage stock level and catalogue'}
          </p>
        </div>

        {!isStaff && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm shrink-0"
            id="add-accessory-btn"
          >
            <Plus className="w-4 h-4" />
            <span>Add Item</span>
          </button>
        )}
      </div>

      {/* Search & Filters */}
      <div className={`grid grid-cols-1 ${isOwner ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3`}>
        <div className={isOwner ? 'sm:col-span-2' : 'sm:col-span-2'}>
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search accessory name, SKU, category..."
          />
        </div>
        {isOwner && (
          <div>
            <select
              value={archiveFilter}
              onChange={(e) => {
                setArchiveFilter(e.target.value as 'active' | 'archived' | 'all');
                setPage(0);
              }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="active">Active Only (Default)</option>
              <option value="archived">Archived Items</option>
              <option value="all">All (Active + Archived)</option>
            </select>
          </div>
        )}
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(0);
            }}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <option value="">All Categories</option>
            <option value="Charger & Power">Charger & Power</option>
            <option value="RAM & Memory">RAM & Memory</option>
            <option value="SSD & Storage">SSD & Storage</option>
            <option value="Bag & Sleeve">Bag & Sleeve</option>
            <option value="Mouse & Keyboard">Mouse & Keyboard</option>
            <option value="Cable & Adapter">Cable & Adapter</option>
            <option value="Screen & Spare Parts">Screen & Spare Parts</option>
            <option value="Other Accessories">Other Accessories</option>
          </select>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => {
              setLowStockOnly(!lowStockOnly);
              setPage(0);
            }}
            className={`w-full py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center space-x-2 border transition-all ${
              lowStockOnly
                ? 'bg-red-50 border-brand-red text-brand-red ring-1 ring-brand-red'
                : 'bg-white border-brand-border text-brand-black hover:bg-brand-bg'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span>Low Stock Filter {lowStockOnly ? '(ON)' : ''}</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Table */}
      <div className="bg-white rounded-lg border border-brand-border shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-bg border-b border-brand-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                  Item / Category
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden sm:table-cell">
                  SKU
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                  Quantity
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                  Selling Price
                </th>
                {canViewCosts && (
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden lg:table-cell">
                    Avg Cost
                  </th>
                )}
                <th className="px-4 py-3" />
              </tr>
            </thead>

            {isLoading ? (
              <TableSkeleton rows={8} cols={canViewCosts ? 6 : 5} />
            ) : accessories.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={canViewCosts ? 6 : 5}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                        <Package className="w-8 h-8 text-brand-green" />
                      </div>
                      <p className="text-base font-bold text-brand-black">No accessories found</p>
                      <p className="text-xs text-brand-muted mt-1 max-w-xs">
                        {debouncedSearch || categoryFilter || lowStockOnly
                          ? 'No items match your active search filters.'
                          : 'Add your first accessory to catalogue inventory.'}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-brand-border">
                {accessories.map((item: Accessory) => {
                  const isLowStock = item.quantity <= item.reorder_level;
                  return (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedAccessoryId(item.id)}
                      className={`hover:bg-brand-bg cursor-pointer transition-colors ${
                        item.archived_at
                          ? 'bg-slate-50/60 opacity-85'
                          : isLowStock
                          ? 'bg-red-50/40'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            item.archived_at
                              ? 'bg-slate-150 text-slate-700'
                              : isLowStock
                              ? 'bg-red-100 text-red-800'
                              : 'bg-brand-green/10 text-brand-green'
                          }`}>
                            <Package className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-semibold text-brand-black">{item.name}</p>
                              {item.archived_at && (
                                <span className="px-1.5 py-0.2 bg-slate-200 text-slate-800 text-[10px] font-bold rounded uppercase">
                                  Archived
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-brand-muted">{item.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-brand-muted hidden sm:table-cell font-mono">
                        {item.sku || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1.5">
                          <span
                            className={`font-bold text-xs px-2 py-0.5 rounded-full ${
                              isLowStock ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {item.quantity} in stock
                          </span>
                          {isLowStock && (
                            <AlertTriangle className="w-3.5 h-3.5 text-brand-red shrink-0" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-brand-green text-sm">
                          Rs. {item.selling_price.toLocaleString('en-PK')}
                        </span>
                      </td>
                      {canViewCosts && (
                        <td className="px-4 py-3 text-xs text-brand-muted hidden lg:table-cell">
                          {item.average_cost != null ? `Rs. ${item.average_cost.toLocaleString('en-PK')}` : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-brand-green font-semibold hover:underline">
                          View →
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        <Pagination
          page={page}
          totalCount={totalCount}
          onPageChange={(p) => setPage(p)}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <Modal isOpen title="Add Catalogue Item" onClose={() => setShowAddModal(false)}>
          <AccessoryForm onSuccess={() => setShowAddModal(false)} onCancel={() => setShowAddModal(false)} />
        </Modal>
      )}

      {/* Item Detail Modal */}
      {selectedAccessoryId && (
        <Modal isOpen title="Accessory Catalogue & Stock" onClose={() => setSelectedAccessoryId(null)}>
          <AccessoryDetail accessoryId={selectedAccessoryId} onClose={() => setSelectedAccessoryId(null)} />
        </Modal>
      )}
    </div>
  );
};
